import { useEffect, useRef, useState } from 'react'
import { PIN_LENGTH } from '@/lib/pin-policy'
import { cn } from '@/utils/cn'

/**
 * Four masked PIN cells over one real numeric input (so paste, backspace and
 * phone number pads behave). `onComplete` fires when the 4th digit is typed.
 * Bump `shakeKey` to shake + clear (wrong PIN).
 *
 * The input is deliberately NOT type="password": browsers treat any password
 * field as a sign-in password and offer to save the PIN. It is invisible
 * (opacity 0) and the cells only show dots, so plain text is safe here.
 */
export function PinInput({
  value,
  onChange,
  onComplete,
  disabled,
  error,
  shakeKey = 0,
  autoFocus = true,
  label = 'PIN',
}: {
  value: string
  onChange: (value: string) => void
  onComplete?: (value: string) => void
  disabled?: boolean
  error?: boolean
  shakeKey?: number
  autoFocus?: boolean
  label?: string
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [focused, setFocused] = useState(false)

  useEffect(() => {
    if (autoFocus && !disabled) inputRef.current?.focus()
  }, [autoFocus, disabled, shakeKey])

  const handle = (raw: string) => {
    const next = raw.replace(/\D/g, '').slice(0, PIN_LENGTH)
    onChange(next)
    if (next.length === PIN_LENGTH) onComplete?.(next)
  }

  return (
    // Re-keyed on every wrong attempt so the shake animation restarts.
    <div
      key={shakeKey}
      className={cn('relative mx-auto w-fit', shakeKey > 0 && 'animate-shake')}
      onClick={() => inputRef.current?.focus()}
    >
      <input
        ref={inputRef}
        type="text"
        name="pin-code"
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
        // Password managers: this is not a password.
        data-1p-ignore=""
        data-lpignore="true"
        data-form-type="other"
        maxLength={PIN_LENGTH}
        aria-label={label}
        disabled={disabled}
        value={value}
        onChange={(e) => handle(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        className="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0"
      />
      <div className="flex gap-3" aria-hidden>
        {Array.from({ length: PIN_LENGTH }, (_, i) => {
          const filled = i < value.length
          const active = focused && i === Math.min(value.length, PIN_LENGTH - 1)
          return (
            <span
              key={i}
              className={cn(
                'flex h-14 w-12 items-center justify-center rounded-2xl border-2 bg-background transition-all duration-150',
                error
                  ? 'border-destructive/70'
                  : active
                    ? 'border-primary shadow-[0_0_0_4px] shadow-primary/15'
                    : filled
                      ? 'border-primary/50'
                      : 'border-border',
                disabled && 'opacity-60',
              )}
            >
              <span
                className={cn(
                  'h-3 w-3 rounded-full bg-foreground transition-transform duration-150',
                  filled ? 'scale-100' : 'scale-0',
                )}
              />
            </span>
          )
        })}
      </div>
    </div>
  )
}
